import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Modal } from './Modal';
import { Button } from './Button';

export interface ColumnDef {
  id: string;
  label: string;
  /** Shown by default the first time the user opens the page. */
  defaultOn: boolean;
  /** Cannot be hidden (e.g. the primary "name" column). */
  always?: boolean;
}

interface ColumnDisplayModalProps {
  open: boolean;
  onClose: () => void;
  columns: ColumnDef[];
  visible: Record<string, boolean>;
  onApply: (next: Record<string, boolean>) => void;
  title?: string;
}

/**
 * Generic "Column display" modal — lets the user choose which table columns
 * are visible. Mirrors the PO's proto (`posColDisplayModal` / `POS_COLUMNS`)
 * behaviour: toggle individual columns, "show all", and an explicit Apply.
 */
export const ColumnDisplayModal: React.FC<ColumnDisplayModalProps> = ({
  open,
  onClose,
  columns,
  visible,
  onApply,
  title = 'Column display'
}) => {
  const [draft, setDraft] = useState(visible);

  useEffect(() => {
    if (open) setDraft(visible);
  }, [open, visible]);

  const visibleCount = Object.values(draft).filter(Boolean).length;
  const allOn = visibleCount === columns.length;

  const toggle = (id: string) =>
    setDraft((prev) => ({ ...prev, [id]: !prev[id] }));

  const toggleAll = () => {
    const next: Record<string, boolean> = {};
    columns.forEach((c) => {
      next[c.id] = !allOn || Boolean(c.always);
    });
    setDraft(next);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      subtitle={`Choose which columns appear in the table (${columns.length} available).`}
      maxWidth="560px"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => {
              onApply(draft);
              onClose();
            }}
          >
            Apply ({visibleCount})
          </Button>
        </>
      }
    >
      <ShowAllRow>
        <ShowAllLabel>
          <input type="checkbox" checked={allOn} onChange={toggleAll} />
          Show all columns
        </ShowAllLabel>
      </ShowAllRow>
      <Grid>
        {columns.map((c) => (
          <Row key={c.id} $checked={Boolean(draft[c.id])} $locked={c.always}>
            <input
              type="checkbox"
              checked={Boolean(draft[c.id])}
              disabled={c.always}
              onChange={() => toggle(c.id)}
            />
            <span>{c.label}</span>
          </Row>
        ))}
      </Grid>
    </Modal>
  );
};

const ShowAllRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--line-soft, #e8e5df);
`;

const ShowAllLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: var(--ink-muted);
  cursor: pointer;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 16px;
`;

const Row = styled.label<{ $checked?: boolean; $locked?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 13px;
  color: var(--ink);
  cursor: ${({ $locked }) => ($locked ? 'default' : 'pointer')};
  background: ${({ $checked }) =>
    $checked ? 'var(--terracotta-soft, #fef3ee)' : 'transparent'};
  opacity: ${({ $locked }) => ($locked ? 0.6 : 1)};
`;
